import type { LibraryEntry, LibrarySyncMutation, LibrarySyncState, MediaSummary, PlaybackRecord } from '../../shared/contracts'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { api, ApiError } from '@/services/api'
import { applyLibrarySyncMutations, coalesceLibrarySyncMutations, compactMediaSummary, compactSyncPayload, MAX_HISTORY_ITEMS } from '../../shared/sync'

const SYNC_DEBOUNCE_MS = 800
const SYNC_RETRY_BASE_MS = 2_000
const SYNC_RETRY_MAX_MS = 60_000
const SYNC_START_RETRY_BASE_MS = 2_000
const SYNC_START_RETRY_MAX_MS = 60_000
const REFRESH_THROTTLE_MS = 3_000

function mediaKey(media: Pick<MediaSummary, 'sourceId' | 'id'>): string {
    return `${media.sourceId}:${media.id}`
}

function removeLegacyLibraryCache() {
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
        const key = localStorage.key(index)
        if (key === 'cineflow:library:v2' || key?.startsWith('cineflow:library:')) { localStorage.removeItem(key) }
    }
}

function isOnline() {
    return typeof navigator === 'undefined' || navigator.onLine !== false
}

function isRetryableSyncError(error: unknown) {
    return !(error instanceof ApiError) || error.status === 0 || error.status === 408 || error.status === 409 || error.status === 429 || error.status >= 500
}

function createMutationId() {
    if (typeof crypto.randomUUID === 'function') { return crypto.randomUUID() }
    const bytes = new Uint8Array(16)
    crypto.getRandomValues(bytes)
    return Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('')
}

export const useLibraryStore = defineStore('library', () => {
    removeLegacyLibraryCache()
    const identity = ref<string | null>(null)
    const favorites = ref<LibraryEntry[]>([])
    const history = ref<PlaybackRecord[]>([])
    const cloudSyncReady = ref(false)
    const favoriteKeys = computed(() => new Set(favorites.value.map(item => item.key)))
    let cloudRevision = 0
    let cloudSyncStarted = false
    let cloudSyncPromise: Promise<void> | undefined
    let cloudSyncTimer: number | undefined
    let cloudOperation: Promise<void> = Promise.resolve()
    let pendingMutations: LibrarySyncMutation[] = []
    let refreshPromise: Promise<boolean> | undefined
    let lastRefreshAt = 0
    let syncEpoch = 0
    let syncRetryAttempt = 0
    let syncRetryAt = 0
    let syncStartRetryAttempt = 0
    let syncStartRetryTimer: number | undefined
    let syncAutoRetryPaused = false
    let onlineListenerAttached = false

    function runCloudOperation<T>(operation: () => Promise<T>): Promise<T> {
        const result = cloudOperation.then(operation, operation)
        cloudOperation = result.then(() => undefined, () => undefined)
        return result
    }

    function applyCloudState(state: LibrarySyncState) {
        if (state.revision < cloudRevision) { return }
        const merged = pendingMutations.length
            ? applyLibrarySyncMutations(state.data || undefined, pendingMutations)
            : compactSyncPayload(state.data || {})
        favorites.value = merged.favorites
        history.value = merged.history
        cloudRevision = state.revision
    }

    function resetSyncRetry() {
        syncRetryAttempt = 0
        syncRetryAt = 0
        syncAutoRetryPaused = false
    }

    function resetStartRetry() {
        window.clearTimeout(syncStartRetryTimer)
        syncStartRetryTimer = undefined
        syncStartRetryAttempt = 0
    }

    function scheduleStartRetry(error: unknown, syncIdentity: string, epoch: number) {
        if (!isRetryableSyncError(error) || !isOnline()) { return }
        const exponent = Math.min(syncStartRetryAttempt, 5)
        const delay = Math.min(SYNC_START_RETRY_BASE_MS * 2 ** exponent, SYNC_START_RETRY_MAX_MS)
        syncStartRetryAttempt = Math.min(syncStartRetryAttempt + 1, 5)
        window.clearTimeout(syncStartRetryTimer)
        syncStartRetryTimer = window.setTimeout(() => {
            syncStartRetryTimer = undefined
            if (identity.value === syncIdentity && syncEpoch === epoch && !cloudSyncStarted && !syncAutoRetryPaused && isOnline()) {
                void startCloudSync()
            }
        }, delay)
    }

    function scheduleSync(delay = SYNC_DEBOUNCE_MS) {
        if (!cloudSyncStarted || !cloudSyncReady.value || !pendingMutations.length || syncAutoRetryPaused || !isOnline()) { return }
        const retryDelay = Math.max(0, syncRetryAt - Date.now())
        window.clearTimeout(cloudSyncTimer)
        cloudSyncTimer = window.setTimeout(() => void pushSync().catch(() => {}), Math.max(delay, retryDelay))
    }

    function scheduleRetry(error: unknown) {
        if (!isRetryableSyncError(error)) {
            syncAutoRetryPaused = true
            return
        }
        if (!isOnline()) { return }
        const exponent = Math.min(syncRetryAttempt, 5)
        const delay = Math.min(SYNC_RETRY_BASE_MS * 2 ** exponent, SYNC_RETRY_MAX_MS)
        syncRetryAttempt = Math.min(syncRetryAttempt + 1, 5)
        syncRetryAt = Date.now() + delay
        scheduleSync(0)
    }

    function handleOnline() {
        if (!identity.value || syncAutoRetryPaused) { return }
        resetSyncRetry()
        resetStartRetry()
        if (!cloudSyncStarted) {
            void startCloudSync()
            return
        }
        scheduleSync(0)
    }

    function attachOnlineListener() {
        if (onlineListenerAttached) { return }
        window.addEventListener('online', handleOnline)
        onlineListenerAttached = true
    }

    function detachOnlineListener() {
        if (!onlineListenerAttached) { return }
        window.removeEventListener('online', handleOnline)
        onlineListenerAttached = false
    }

    function enqueueMutation(mutation: LibrarySyncMutation) {
        if (!identity.value) { return }
        const queued = mutation.id ? mutation : { ...mutation, id: createMutationId() }
        pendingMutations = coalesceLibrarySyncMutations(pendingMutations, queued)
        scheduleSync()
    }

    function setIdentity(userId?: string | null) {
        const nextIdentity = userId || null
        if (identity.value === nextIdentity) { return }
        stopCloudSync()
        identity.value = nextIdentity
        favorites.value = []
        history.value = []
    }

    function isFavorite(media: Pick<MediaSummary, 'sourceId' | 'id'>) {
        return favoriteKeys.value.has(mediaKey(media))
    }

    function toggleFavorite(media: MediaSummary) {
        const key = mediaKey(media)
        const index = favorites.value.findIndex(item => item.key === key)
        if (index >= 0) {
            favorites.value.splice(index, 1)
            enqueueMutation({ type: 'favorite-delete', key })
            return
        }
        const compacted = compactMediaSummary(media)
        if (!compacted) { return }
        const entry = { key, media: compacted, updatedAt: Date.now() }
        favorites.value.unshift(entry)
        enqueueMutation({ type: 'favorite-upsert', entry })
    }

    function recordProgress(media: MediaSummary, episodeIndex: number, episodeLabel: string, position: number, duration: number) {
        if (!Number.isFinite(position) || position < 0) { return }
        const key = mediaKey(media)
        const compacted = compactMediaSummary(media)
        if (!compacted) { return }
        const record: PlaybackRecord = {
            key,
            media: compacted,
            episodeIndex,
            episodeLabel,
            position,
            duration: Number.isFinite(duration) ? duration : 0,
            progress: duration > 0 ? Math.min(position / duration, 1) : 0,
            updatedAt: Date.now(),
        }
        history.value = [record, ...history.value.filter(item => item.key !== key)].slice(0, MAX_HISTORY_ITEMS)
        enqueueMutation({ type: 'history-upsert', record })
    }

    function getRecord(media: Pick<MediaSummary, 'sourceId' | 'id'>) {
        return history.value.find(item => item.key === mediaKey(media))
    }

    function removeHistory(key: string) {
        history.value = history.value.filter(item => item.key !== key)
        enqueueMutation({ type: 'history-delete', key })
    }

    function clearHistory() {
        history.value = []
        enqueueMutation({ type: 'history-clear' })
    }

    async function flushPending(syncIdentity: string): Promise<boolean> {
        if (!pendingMutations.length || identity.value !== syncIdentity) { return false }
        const batch = pendingMutations
        pendingMutations = []
        try {
            const state = await api.putSync(batch)
            if (identity.value === syncIdentity) {
                resetSyncRetry()
                applyCloudState(state)
            }
            return true
        }
        catch (error) {
            if (identity.value === syncIdentity) {
                pendingMutations = [...batch, ...pendingMutations].reduce<LibrarySyncMutation[]>((result, mutation) => coalesceLibrarySyncMutations(result, mutation), [])
                scheduleRetry(error)
            }
            throw error
        }
    }

    async function pushSync() {
        const syncIdentity = identity.value
        if (!syncIdentity || !isOnline() || syncAutoRetryPaused) { return }
        const retryDelay = Math.max(0, syncRetryAt - Date.now())
        if (retryDelay > 0) {
            scheduleSync(retryDelay)
            return
        }
        window.clearTimeout(cloudSyncTimer)
        await runCloudOperation(() => flushPending(syncIdentity))
    }

    async function startCloudSync() {
        if (!identity.value || cloudSyncStarted) { return cloudSyncPromise }
        resetStartRetry()
        cloudSyncStarted = true
        cloudSyncReady.value = false
        attachOnlineListener()
        const syncIdentity = identity.value
        const epoch = syncEpoch
        const task = runCloudOperation(async () => {
            try {
                const state = await api.getSync()
                if (!state.database || !cloudSyncStarted || identity.value !== syncIdentity || syncEpoch !== epoch) { return }
                applyCloudState(state)
                cloudSyncReady.value = true
                resetSyncRetry()
                resetStartRetry()
                lastRefreshAt = Date.now()
                scheduleSync()
            }
            catch (error) {
                if (identity.value === syncIdentity && syncEpoch === epoch) {
                    window.clearTimeout(cloudSyncTimer)
                    cloudSyncReady.value = false
                    cloudSyncStarted = false
                    if (!isRetryableSyncError(error)) {
                        syncAutoRetryPaused = true
                        detachOnlineListener()
                    }
                    else { scheduleStartRetry(error, syncIdentity, epoch) }
                }
            }
        })
        cloudSyncPromise = task
        await task
        if (cloudSyncPromise === task) { cloudSyncPromise = undefined }
    }

    async function refreshCloudSync(force = false): Promise<boolean> {
        if (!identity.value) { return false }
        if (!cloudSyncStarted) { await startCloudSync() }
        if (!cloudSyncStarted || !cloudSyncReady.value) { return false }
        if (refreshPromise) { return refreshPromise }
        if (!force && Date.now() - lastRefreshAt < REFRESH_THROTTLE_MS) { return false }
        const syncIdentity = identity.value
        const task = runCloudOperation(async () => {
            await flushPending(syncIdentity)
            const state = await api.getSync()
            if (!state.database || identity.value !== syncIdentity || !cloudSyncStarted) { return false }
            applyCloudState(state)
            lastRefreshAt = Date.now()
            scheduleSync()
            return Boolean(state.data)
        })
        refreshPromise = task
        try { return await task }
        finally { if (refreshPromise === task) { refreshPromise = undefined } }
    }

    function pullSync() {
        return refreshCloudSync(true)
    }

    function stopCloudSync() {
        syncEpoch += 1
        window.clearTimeout(cloudSyncTimer)
        detachOnlineListener()
        cloudSyncPromise = undefined
        refreshPromise = undefined
        cloudSyncStarted = false
        cloudSyncReady.value = false
        cloudRevision = 0
        pendingMutations = []
        lastRefreshAt = 0
        resetSyncRetry()
        resetStartRetry()
    }

    return { identity, favorites, history, cloudSyncReady, setIdentity, isFavorite, toggleFavorite, recordProgress, getRecord, removeHistory, clearHistory, pushSync, pullSync, refreshCloudSync, startCloudSync, stopCloudSync }
})
